import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Receipt,
  IndianRupee,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../../../lib/auth";
import { api } from "../../../../lib/api";
import usePeriodFilter from "@/features/vendor/common/analytics/usePeriodFilter";
import useStorefronts from "@/features/vendor/common/storefronts/useStorefronts";
import { formatINR, timeAgo } from "../../../../lib/utils";
import StatusBadge from "@/shared/components/feedback/StatusBadge";
import StatCard from "@/features/vendor/common/analytics/components/StatCard";
import PeriodFilter from "@/features/vendor/common/analytics/components/PeriodFilter";
import StoreFilter from "@/features/vendor/common/analytics/components/StoreFilter";
import PerStoreBreakdown from "@/features/vendor/common/analytics/components/PerStoreBreakdown";

function formatRevenueTooltipDate(iso) {
  const date = new Date(`${iso}T12:00:00`);
  const day = date.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? "st"
    : day % 10 === 2 && day !== 12 ? "nd"
      : day % 10 === 3 && day !== 13 ? "rd" : "th";
  const monthAndYear = date.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  return `${day}${suffix} ${monthAndYear}`;
}

/**
 * OverviewPage — the owner's consolidated multi-store dashboard.
 *
 * All heavy-lifting (aggregation, filtering) lives in hooks. This file is
 * responsible only for choosing filters and arranging presentational pieces.
 */
export default function OverviewPage() {
  const { vendor } = useAuth();
  const { storefronts } = useStorefronts();
  const period = usePeriodFilter("30d");
  const [storeFilter, setStoreFilter] = useState("all");

  const storefrontIds = storeFilter === "all" ? undefined : [storeFilter];
  const [analytics, setAnalytics] = useState(null);
  const [analyticsError, setAnalyticsError] = useState("");
  useEffect(() => {
    let active = true;
    setAnalyticsError("");
    api.getDashboardSummary({ from: period.from, to: period.to, storefrontId: storefrontIds?.[0] })
      .then((summary) => {
        if (!active) return;
        setAnalytics({
          kpis: summary.kpis || { revenue: 0, orders: 0 },
          perStore: summary.perStore || [],
          trend: (summary.trend || []).map((day) => ({ ...day, value: Number(day.revenue) || 0 })),
          orders: summary.recentOrders || [],
        });
      })
      .catch((error) => {
        if (!active) return;
        setAnalyticsError(error?.message || "Dashboard data could not be loaded.");
        setAnalytics({ kpis: { revenue: 0, orders: 0 }, perStore: [], trend: [], orders: [] });
      });
    return () => { active = false; };
  }, [period.from, period.to, storefrontIds?.[0]]);

  const isMultiStore = vendor?.features?.multiStore && storefronts.length > 1;

  if (!analytics) return <div className="card-surface p-6 text-sm text-ink-secondary">Loading dashboard…</div>;
  const { kpis, perStore, trend, orders } = analytics;
  const trendMax = Math.max(...trend.map((d) => d.value), 1);

  const stats = [
    { icon: IndianRupee, label: "Revenue", value: formatINR(kpis.revenue), tint: "text-brand", ring: "border-brand/30 bg-brand-soft" },
    { icon: Receipt, label: "Orders", value: kpis.orders, tint: "text-restaurant", ring: "border-orange-500/30 bg-orange-500/10" },
  ];
  const recentOrders = orders;

  return (
    <div className="min-w-0 space-y-6 sm:space-y-8" data-testid="overview-responsive-root">
      {/* Header */}
      <div className="flex min-w-0 flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-ink-muted">Dashboard</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-display font-medium tracking-tight break-words" data-testid="overview-title">
            Good to see you, {(vendor.name || "").split(" ")[0] || "there"} 👋
          </h1>
          <p className="mt-1 text-ink-secondary">
            {isMultiStore
              ? `Consolidated view across ${storefronts.length} storefronts.`
              : "Revenue and orders for the selected period."}
          </p>
        </div>
        <div className="flex max-w-full items-center flex-wrap gap-2">
          <StoreFilter
            storefronts={storefronts}
            value={storeFilter}
            onChange={setStoreFilter}
            testid="overview-store-filter"
          />
          <PeriodFilter
            options={period.options}
            value={period.period}
            onChange={period.setPeriod}
            testid="overview-period-filter"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-3" data-testid="overview-kpi-grid">
        {stats.map((s, i) => (
          <StatCard key={s.label} {...s} index={i} testid={`stat-card-${i}`} />
        ))}
      </div>
      {analyticsError && <div className="text-sm text-red-400" role="alert">{analyticsError}</div>}

      {/* Multi-store breakdown */}
      {isMultiStore && (
        <PerStoreBreakdown stats={perStore} onSelect={setStoreFilter} />
      )}

      {/* Trend + recent */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="min-w-0 lg:col-span-2 card-surface p-4 sm:p-5 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-ink-muted">{period.currentLabel}</div>
              <div className="mt-1 font-display text-lg font-medium">Revenue trend</div>
            </div>
            <div className="text-sm text-ink-secondary">
              Total <span className="text-ink-primary font-medium">{formatINR(trend.reduce((s, d) => s + d.value, 0))}</span>
            </div>
          </div>
          {trend.length === 0 ? (
            <div className="text-sm text-ink-muted py-10 text-center">Not enough data.</div>
          ) : (
            <div className="flex h-40 min-w-0 items-end gap-[3px] overflow-hidden border-b border-line" data-testid="overview-trend-chart">
              {trend.map((d, i) => (
                <div
                  key={d.iso || i}
                  className="flex-1 rounded-t bg-gradient-to-t from-brand/25 to-brand hover:from-brand/50 hover:to-brand transition-colors"
                  style={{ height: `${d.value === 0 ? 0 : Math.max((d.value / trendMax) * 140, 2)}px` }}
                  title={`${formatRevenueTooltipDate(d.iso)}, ₹${Math.round(Number(d.value) || 0)}`}
                  aria-label={`${formatRevenueTooltipDate(d.iso)}, ₹${Math.round(Number(d.value) || 0)}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0 card-surface p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg font-medium">Recent activity</div>
            <Link
              to="/dashboard/orders"
              className="text-xs text-brand hover:text-brand-hover inline-flex items-center gap-1 transition-colors"
              data-testid="overview-view-all"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="text-sm text-ink-muted py-8 text-center">Nothing yet.</div>
          ) : (
            <ul className="space-y-2">
              {recentOrders.map((r) => (
                <li key={r.id} className="rounded-lg bg-bg-elevated border border-line p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono flex items-center gap-1.5">
                        <Receipt className="h-2.5 w-2.5" />
                        {r.code || r.orderNumber}
                      </div>
              <div className="mt-0.5 text-sm truncate">
                        {(r.items || []).map((it) => `${it.name} ×${it.qty ?? it.quantity}`).join(", ") || "Order"}
                      </div>
                      <div className="mt-1 text-[11px] text-ink-muted">
                        {r.customer?.name || "—"} · {timeAgo(r.createdAt)}
                      </div>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {vendor?.slug && (
        <a
          href={`/store/${vendor.slug}`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost inline-flex items-center gap-2 self-start"
          data-testid="overview-view-store"
        >
          <ExternalLink className="h-4 w-4" /> View storefront
        </a>
      )}
    </div>
  );
}
